import { supabase } from '@/lib/supabase'
import { normalizeError } from './errors'
import type { Service, GalleryService, GalleryServiceWithDetails } from '../types'

/**
 * Fetch all available services ordered by title.
 * Used for populating filter dropdowns and service selectors.
 *
 * @note Uses existing services table which has `title` field (not `name`)
 */
export async function fetchServices(): Promise<Service[]> {
  try {
    const { data, error } = await supabase
      .from('services')
      .select('id, title, slug, description, created_at, updated_at')
      .order('title', { ascending: true })
    if (error) throw error
    return data || []
  } catch (err) {
    throw normalizeError(err, 'Failed to fetch services')
  }
}

/**
 * Fetch services linked to a specific gallery with full service details.
 * Returns services ordered by is_primary (primary first) then by service sort_order.
 *
 * @param galleryId - The gallery ID to fetch services for
 * @see supabase/migrations/20251204_create_gallery_services_table.sql - Junction table
 */
export async function fetchGalleryServices(galleryId: number): Promise<GalleryServiceWithDetails[]> {
  try {
    const { data, error } = await supabase
      .from('gallery_services')
      .select(`
        gallery_id,
        service_id,
        is_primary,
        created_at,
        service:services(*)
      `)
      .eq('gallery_id', galleryId)
      .order('is_primary', { ascending: false })

    if (error) throw error

    // Transform the nested service object to match GalleryServiceWithDetails
    return (data || []).map(row => ({
      gallery_id: row.gallery_id,
      service_id: row.service_id,
      is_primary: row.is_primary,
      created_at: row.created_at,
      service: (Array.isArray(row.service) ? row.service[0] : row.service) as unknown as Service
    }))
  } catch (err) {
    throw normalizeError(err, 'Failed to fetch gallery services')
  }
}

/**
 * Add a service to a gallery.
 * If isPrimary is true, the database trigger will automatically unset
 * any existing primary service for this gallery.
 *
 * @param galleryId - The gallery to add the service to
 * @param serviceId - The service UUID to add
 * @param isPrimary - Whether this should be the primary service
 */
export async function addServiceToGallery(
  galleryId: number,
  serviceId: string,
  isPrimary = false
): Promise<GalleryService> {
  try {
    const { data, error } = await supabase
      .from('gallery_services')
      .insert({
        gallery_id: galleryId,
        service_id: serviceId,
        is_primary: isPrimary
      })
      .select()
      .single()
    if (error) throw error
    return data
  } catch (err) {
    throw normalizeError(err, 'Failed to add service to gallery')
  }
}

/**
 * Remove a service from a gallery.
 *
 * @param galleryId - The gallery to remove the service from
 * @param serviceId - The service UUID to remove
 */
export async function removeServiceFromGallery(
  galleryId: number,
  serviceId: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('gallery_services')
      .delete()
      .eq('gallery_id', galleryId)
      .eq('service_id', serviceId)
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to remove service from gallery')
  }
}

/**
 * Set a service as the primary service for a gallery.
 * The database trigger will automatically unset any existing primary.
 *
 * @param galleryId - The gallery to update
 * @param serviceId - The service UUID to set as primary
 */
export async function setPrimaryService(
  galleryId: number,
  serviceId: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('gallery_services')
      .update({ is_primary: true })
      .eq('gallery_id', galleryId)
      .eq('service_id', serviceId)
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to set primary service')
  }
}

/**
 * Bulk update services for a gallery.
 * Replaces all existing service associations with the provided list.
 *
 * @param galleryId - The gallery to update
 * @param serviceIds - Array of service UUIDs to associate
 * @param primaryServiceId - Optional UUID of the primary service
 */
export async function updateGalleryServices(
  galleryId: number,
  serviceIds: string[],
  primaryServiceId?: string
): Promise<void> {
  try {
    // Delete existing associations
    const { error: deleteError } = await supabase
      .from('gallery_services')
      .delete()
      .eq('gallery_id', galleryId)
    if (deleteError) throw deleteError

    // Insert new associations if any
    if (serviceIds.length > 0) {
      const insertData = serviceIds.map(serviceId => ({
        gallery_id: galleryId,
        service_id: serviceId,
        is_primary: serviceId === primaryServiceId
      }))

      const { error: insertError } = await supabase
        .from('gallery_services')
        .insert(insertData)
      if (insertError) throw insertError
    }
  } catch (err) {
    throw normalizeError(err, 'Failed to update gallery services')
  }
}

/**
 * Create a new service (admin feature).
 * Auto-generates slug from title.
 *
 * @param title - Display title for the service
 * @param description - Optional description for tooltips/SEO
 */
export async function createService(
  title: string,
  description?: string
): Promise<Service> {
  try {
    // Generate slug from title: lowercase, spaces to hyphens, remove special chars
    const slug = title
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')

    const { data, error } = await supabase
      .from('services')
      .insert({
        title,
        slug,
        description: description || null
      })
      .select('id, title, slug, description, created_at, updated_at')
      .single()

    if (error) throw error
    return data
  } catch (err) {
    throw normalizeError(err, 'Failed to create service')
  }
}

/**
 * Update an existing service.
 *
 * @param serviceId - The service UUID to update
 * @param updates - Partial service fields to update
 * @note Uses `title` field to match existing services table schema
 */
export async function updateService(
  serviceId: string,
  updates: Partial<Pick<Service, 'title' | 'description'>>
): Promise<Service> {
  try {
    // If title is being updated, regenerate slug
    const patchData: Record<string, unknown> = { ...updates }
    if (updates.title) {
      patchData.slug = updates.title
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
    }

    const { data, error } = await supabase
      .from('services')
      .update(patchData)
      .eq('id', serviceId)
      .select('id, title, slug, description, created_at, updated_at')
      .single()

    if (error) throw error
    return data
  } catch (err) {
    throw normalizeError(err, 'Failed to update service')
  }
}

/**
 * Delete a service.
 * Note: This will cascade delete all gallery_services associations.
 *
 * @param serviceId - The service UUID to delete
 */
export async function deleteService(serviceId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('services')
      .delete()
      .eq('id', serviceId)
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to delete service')
  }
}
